// Tenant-timezone date formatter. The active tenant timezone is set once by
// BusinessContext on tenant load; all Business CRM date displays render through this.
// Fallback is browser-local (current behavior) when timezone is missing or invalid.
let _tz = null;

export function setTenantTimezone(tz) {
  _tz = tz || null;
}

export function formatInTenantTz(value, opts) {
  if (value == null || value === '') return '';
  try {
    return new Date(value).toLocaleString(undefined, { ...(opts || {}), timeZone: _tz || undefined });
  } catch {
    try { return new Date(value).toLocaleString(); } catch { return String(value); }
  }
}

// ---------------------------------------------------------------------------------------------
// TENANT-ZONE WALL CLOCK <-> UTC INSTANT
//
// `formatInTenantTz` above only RENDERS. The Blackout-window editor needs the inverse: an operator
// picks "27 Sep, 19:00" meaning 19:00 AT THE CLINIC, and that must persist as the correct UTC instant
// no matter where the operator's browser is. The editor previously used `new Date(v).toISOString()`,
// which interprets the typed value in the BROWSER's zone -- so the same entry saved a different real
// instant depending on who was signed in, and the tenant's own timezone was never consulted.
//
// Implemented with Intl only (no new dependency). The offset is resolved AT the instant in question,
// so DST is handled: the second pass re-resolves when the first guess landed on the other side of a
// transition, which is the case a fixed offset gets wrong twice a year.
// ---------------------------------------------------------------------------------------------

// DELIBERATELY ABSENT: a browser-side local<->UTC converter.
//
// One was added here for the blackout editor and then REMOVED, because the browser's tz database and
// the backend's disagree: Node/ICU 2025b still has Alberta observing DST while the backend's tzdata
// 2026c has it on permanent UTC-6 from November 2026. A browser-resolved instant would therefore
// disagree with the availability engine that enforces it. Local->instant resolution for blackouts is
// owned by the backend (scheduling_service.resolve_blackout_local_times), and the server hands the
// client back the tenant-local wall clock to display. Nothing here should be re-added to "help" —
// leaving the function present would invite exactly that regression.

/** Selectable times for a day at the tenant's own scheduling granularity ("HH:mm", 00:00..23:xx). */
export function timeOptionsForGranularity(granularityMinutes) {
  const step = Math.max(1, Number(granularityMinutes) || 15);
  const out = [];
  for (let m = 0; m < 24 * 60; m += step) {
    out.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
  }
  return out;
}

/** "19:00" -> "7:00 PM". DISPLAY ONLY — the value submitted stays the canonical 24-hour wall clock.
 *  A Business user reading "07:00" cannot tell morning from evening; the operator screenshot that
 *  prompted this showed exactly that ambiguity on a blackout that had to mean 7 in the evening. */
export function formatTimeLabel12h(hhmm) {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return String(hhmm || '');
  const suffix = h < 12 ? 'AM' : 'PM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${suffix}`;
}

/** "2026-09-27" -> "Sep 27, 2026". Display only; the stored value stays ISO. */
export function formatDateLabel(dateStr) {
  const [y, mo, d] = String(dateStr || '').split('-').map(Number);
  if (!y || !mo || !d) return '';
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${MONTHS[mo - 1]} ${d}, ${y}`;
}

// ---------------------------------------------------------------------------------------------
// E5 — SCHEDULING DISPLAY IN THE TENANT'S SCHEDULING TIMEZONE, EXPLICITLY LABELLED.
//
// `formatInTenantTz` above is driven by `business.timezone`, set once by BusinessContext. That is the
// right authority for general CRM dates, but it is NOT the scheduling authority: appointments,
// availability and blackouts are all governed by `SchedulingConfig.timezone`, a separate field that a
// tenant sets independently. Rendering an appointment through the wrong one is wrong precisely when
// the two disagree, which is the case nobody notices until a booking is missed.
//
// So scheduling surfaces pass the zone EXPLICITLY, taken from the scheduling config they already
// fetch. This adds no new authority and stores nothing — the canonical value remains the UTC instant,
// and the zone remains SchedulingConfig.timezone. It is a projection, nothing more.

/** Format a UTC instant in an explicit IANA zone. Falls back to the device zone when none is given,
 *  which `zoneAbbrev` then labels honestly rather than implying it is tenant time. */
export function formatInstantInZone(value, tzName, opts) {
  if (value == null || value === '') return '';
  const base = { year: 'numeric', month: 'short', day: 'numeric',
                 hour: 'numeric', minute: '2-digit' };
  try {
    return new Date(value).toLocaleString(undefined, {
      ...base, ...(opts || {}), ...(tzName ? { timeZone: tzName } : {}),
    });
  } catch {
    // An invalid/unknown IANA name must not blank the screen — render in the device zone instead.
    try { return new Date(value).toLocaleString(undefined, base); } catch { return String(value); }
  }
}

/** The short zone name AT that instant ("MDT" vs "MST"), so the label is DST-correct rather than a
 *  fixed string. Returns '' when it cannot be resolved — better no label than a wrong one. */
export function zoneAbbrev(value, tzName) {
  if (value == null || value === '') return '';
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZoneName: 'short', ...(tzName ? { timeZone: tzName } : {}),
    }).formatToParts(new Date(value));
    const p = parts.find((x) => x.type === 'timeZoneName');
    return p ? p.value : '';
  } catch {
    return '';
  }
}

/** "Sep 27, 2026, 7:00 PM MDT" — the instant plus the zone it is being shown in.
 *  The label is the point: without it an operator cannot tell whose clock they are reading. */
export function formatInstantWithZone(value, tzName, opts) {
  const s = formatInstantInZone(value, tzName, opts);
  if (!s) return '';
  const z = zoneAbbrev(value, tzName);
  return z ? `${s} ${z}` : s;
}
